import React, { useState } from 'react';

import HubEditor from '@atlassian/hub-editor';

import { getCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { PRODUCT_HOME_ID } from '@confluence/app-navigation/entry-points/navigationIDs.ts';
import { useCompanyHubOnRouteCreationFlow } from '@confluence/company-hub-utils/entry-points/useCompanyHubOnRouteCreationFlow.ts';
import { useCompanyHubPremiumGate } from '@confluence/company-hub-utils/entry-points/useCompanyHubPremiumGate.ts';
import { COMPANY_HUB_EDIT } from '@confluence/named-routes/entry-points/routes.ts';
import { CompanyHomeBuilderUpsellPage } from '@confluence/change-edition/entry-points/CompanyHomeBuilderUpsellPage.ts';
import { FullPageLoadingScreen } from '@confluence/full-page-loading-screen';
import { usePageContentId } from '@confluence/page-context';
import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { END } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { HubErrorPageComponent } from '@confluence/company-hub-utils/entry-points/HubErrorPageComponent.ts';

import { getCompanyHubPseudoAppRoute } from './getCompanyHubPseudoAppRoute.tsx';
import type { RouteWithNavParamsType } from '../RouteType.ts';

export function getCompanyHubEditorRoute(component: any): RouteWithNavParamsType {
	const CompanyHubEditorRoute = (routeProps: RouteMatch) => {
		const spaceKey = getCompanyHubSpaceKey();
		const [contentId] = usePageContentId();
		const [queriedContentId, setQueriedContentId] = useState<string | undefined | null>(null);
		const { setQueryParams } = useRouteActions();
		const shouldShowPremiumUpsell = useCompanyHubPremiumGate();

		const { isCreatingHub, creationError } = useCompanyHubOnRouteCreationFlow({
			onSuccess: (homepageId) => {
				setQueriedContentId(homepageId);
				setQueryParams({ redirect: 'first-time' });
			},
			onExists: (homepageId) => {
				setQueriedContentId(homepageId);
			},
			route: COMPANY_HUB_EDIT.pattern,
		});

		if (creationError) {
			return <HubErrorPageComponent error={creationError} />;
		}

		if (shouldShowPremiumUpsell) {
			return <CompanyHomeBuilderUpsellPage featureName="companyHub" />;
		}

		// If there is no contentId in SSR then we will just see the FullPageLoadingScreen being rendered
		// So we skip the loading screen component to ensure the HubEditor is rendered instead
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if ((isCreatingHub || (!queriedContentId && !contentId)) && !process.env.REACT_SSR) {
			return <FullPageLoadingScreen />;
		}

		const Component = component;
		return (
			<Component
				{...routeProps}
				params={{
					contentId: contentId ?? (queriedContentId || ''),
					spaceKey,
					contentType: 'pages',
				}}
			/>
		);
	};

	CompanyHubEditorRoute.NAVIGATION_PARAMS = (routeProps: RouteMatch) => {
		const spaceKey = getCompanyHubSpaceKey();
		return {
			Screen: {
				screenEvent: {
					name: 'companyHubEditorScreen',
					id: spaceKey,
					attributes: {
						navdexPointType: END,
						screenIsInSpace: false,
					},
				},
				pageState: { spaceKey, contentId: routeProps?.query?.contentId },
			},
			MainLayout: {
				navView: PRODUCT_HOME_ID,
			},
		};
	};

	return getCompanyHubPseudoAppRoute(CompanyHubEditorRoute);
}

export const CompanyHubEditorRouteForSSR: RouteWithNavParamsType =
	getCompanyHubEditorRoute(HubEditor);
